/**
 * Retenção de dados (seção 14) — pendência P09.
 *
 * A tela existe para a loja DECIDIR, não para nós decidirmos por ela. Enquanto
 * ninguém aprovar, o expurgo diário roda em simulação e a tela diz isso com
 * todas as letras. Aprovar liga uma exclusão recorrente que não tem desfazer,
 * por isso a confirmação é digitada, não um clique.
 */
import { useEffect, useState } from 'react';
import { useResource } from '../../lib/useResource.js';
import { post } from '../../lib/api.js';
import { formatDateTime } from '../../lib/format.js';
import { Alert, Card, Empty, ErrorBox, Field, Modal, useSubmit, useToast } from '../../components/ui.js';
import { useSession } from '../../lib/session.js';

interface Politica {
  messagesMonths: number;
  audioDays: number;
  transcriptsMonths: number;
  appLogsDays: number;
  auditMonths: number;
  aprovada: boolean;
  aprovadaPor: string | null;
  aprovadaEm: string | null;
}
interface ItemDeExpurgo {
  o_que: string;
  regra: string;
  quantidade: number;
  aplicado: boolean;
}
interface Resposta {
  politica: Politica;
  previa: { simulacao: boolean; politicaAprovada: boolean; itens: ItemDeExpurgo[]; total: number };
}

const CAMPOS: { chave: keyof Politica; label: string; unidade: string; hint: string }[] = [
  {
    chave: 'messagesMonths',
    label: 'Mensagens de conversa',
    unidade: 'meses',
    hint: 'O texto trocado com o cliente no WhatsApp.',
  },
  {
    chave: 'audioDays',
    label: 'Referência dos áudios',
    unidade: 'dias',
    hint: 'O arquivo fica com o provedor; aqui some a ligação para ele.',
  },
  {
    chave: 'transcriptsMonths',
    label: 'Transcrições de áudio',
    unidade: 'meses',
    hint: 'O texto gerado a partir do áudio.',
  },
  {
    chave: 'appLogsDays',
    label: 'Registros técnicos',
    unidade: 'dias',
    hint: 'Eventos do WhatsApp já processados, tentativas de push e de acesso.',
  },
  {
    chave: 'auditMonths',
    label: 'Auditoria',
    unidade: 'meses',
    hint: 'Quem alterou preço, política e cadastro. O prazo mais longo, de propósito.',
  },
];

const CONFIRMACAO = 'APROVAR';

export function Retencao() {
  const { can } = useSession();
  const toast = useToast();
  // Retenção é decisão de quem responde pela LGPD, não do dia a dia da loja.
  const podeLer = can('company.danger_zone');
  const podeAprovar = can('company.danger_zone');

  const { data, error, reload, loading } = useResource<Resposta>(podeLer ? '/api/ops/retencao' : null);
  const [rascunho, setRascunho] = useState<Partial<Politica>>({});
  const [motivo, setMotivo] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [modalAberto, setModalAberto] = useState(false);

  useEffect(() => {
    if (data) setRascunho({});
  }, [data]);

  const valorDe = (chave: keyof Politica): number =>
    (rascunho[chave] as number | undefined) ?? (data?.politica[chave] as number) ?? 0;

  const aprovacao = useSubmit(async () => {
    await post('/api/ops/retencao/politica', {
      ...CAMPOS.reduce<Record<string, number>>((acc, c) => {
        acc[c.chave] = valorDe(c.chave);
        return acc;
      }, {}),
      motivo,
    });
    setModalAberto(false);
    setMotivo('');
    setConfirmacao('');
    toast('Política de retenção aprovada.');
    reload();
  });

  const expurgo = useSubmit(async () => {
    const r = await post<{ total: number; simulacao: boolean }>('/api/ops/retencao/executar', {});
    toast(
      r.simulacao
        ? 'Nada foi apagado: a política ainda não está aprovada.'
        : `Expurgo concluído: ${r.total} registro(s) tratados.`,
    );
    reload();
  });

  if (!podeLer) {
    return (
      <Card>
        <Empty title="Você não tem permissão para ver a retenção de dados." />
      </Card>
    );
  }

  const politica = data?.politica;
  const previa = data?.previa;

  return (
    <>
      <ErrorBox error={error} />

      {politica && !politica.aprovada ? (
        <Alert tone="warning" title="Política ainda não aprovada (pendência P09)">
          Os prazos abaixo são uma proposta. Enquanto a loja não aprovar, a rotina diária apenas
          calcula o que seria apagado e não apaga nada. Apagar dado de cliente por engano não tem
          desfazer.
        </Alert>
      ) : null}

      {politica?.aprovada ? (
        <Alert tone="info" title="Política aprovada">
          Aprovada por {politica.aprovadaPor} em {formatDateTime(politica.aprovadaEm)}. A partir
          daqui a rotina diária apaga de verdade o que passou dos prazos.
        </Alert>
      ) : null}

      <Card
        title="Prazos de retenção"
        description="Nada aqui apaga cliente, veículo, serviço, preço ou lembrete — só conversa, mídia e registro técnico vencidos."
      >
        <div className="grid cols-2">
          {CAMPOS.map((campo) => (
            <Field key={campo.chave} label={`${campo.label} (${campo.unidade})`} hint={campo.hint}>
              <input
                type="number"
                min={1}
                value={valorDe(campo.chave)}
                disabled={!podeAprovar}
                onChange={(e) =>
                  setRascunho((r) => ({ ...r, [campo.chave]: Number(e.target.value) }))
                }
              />
            </Field>
          ))}
        </div>
        {podeAprovar ? (
          <button
            type="button"
            className="button button-primary"
            onClick={() => setModalAberto(true)}
          >
            {politica?.aprovada ? 'Atualizar e reaprovar' : 'Aprovar política'}
          </button>
        ) : (
          <p className="hint">Só o proprietário aprova a política de retenção.</p>
        )}
      </Card>

      <Card
        title="O que seria apagado hoje"
        description="Esta contagem é sempre uma prévia: abrir esta tela nunca apaga nada."
        actions={
          <button type="button" onClick={reload} disabled={loading}>
            Recalcular
          </button>
        }
      >
        {!previa || previa.itens.length === 0 ? (
          <Empty title="Nada a apagar com os prazos atuais." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Dado</th>
                  <th>Regra</th>
                  <th className="num">Registros</th>
                </tr>
              </thead>
              <tbody>
                {previa.itens.map((item) => (
                  <tr key={item.o_que}>
                    <td>{item.o_que}</td>
                    <td className="muted">{item.regra}</td>
                    <td className="num">{item.quantidade}</td>
                  </tr>
                ))}
                <tr>
                  <th colSpan={2}>Total</th>
                  <th className="num">{previa.total}</th>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {podeAprovar && politica?.aprovada ? (
          <>
            <ErrorBox error={expurgo.error} />
            <button type="button" className="button danger" onClick={() => void expurgo.submit()} disabled={expurgo.saving}>
              {expurgo.saving ? 'Apagando…' : 'Executar expurgo agora'}
            </button>
            <p className="hint">
              A rotina diária já faz isso sozinha de madrugada. Este botão serve para antecipar.
            </p>
          </>
        ) : null}
      </Card>

      {modalAberto ? (
        <Modal
          title="Aprovar política de retenção"
          onClose={() => setModalAberto(false)}
          footer={
            <>
              <button type="button" onClick={() => setModalAberto(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="button danger"
                disabled={aprovacao.saving || confirmacao !== CONFIRMACAO || motivo.trim().length < 3}
                onClick={() => void aprovacao.submit()}
              >
                {aprovacao.saving ? 'Aprovando…' : 'Aprovar'}
              </button>
            </>
          }
        >
          <Alert tone="warning" title="Isto liga uma exclusão recorrente">
            A partir da aprovação, todos os dias de madrugada o sistema apaga o que passou dos
            prazos acima. Não há desfazer, e o backup só ajuda se for anterior ao expurgo.
          </Alert>
          <p>
            Com os prazos atuais, o primeiro expurgo trataria <strong>{previa?.total ?? 0}</strong>{' '}
            registro(s).
          </p>
          <Field label="Motivo" required hint="Fica na auditoria, junto com seu nome e a data.">
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: Prazos acordados com a loja em reunião."
            />
          </Field>
          <Field label={`Digite ${CONFIRMACAO} para confirmar`} required>
            <input value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} />
          </Field>
          <ErrorBox error={aprovacao.error} />
        </Modal>
      ) : null}
    </>
  );
}
